import { action, computed, observable } from 'mobx';
import { DnDTypes, TBlock } from '~/types/builder.types';
import {
    IScheduleContainerBlockResponse,
    IScheduleContainerVariableValue,
    ISessionConfig,
    ScheduleContainerVariableClient
} from '~/types/server.types';
import { ContainerableBlock } from './containerable-block.model';
import type { ScheduleSessionBlock } from '~/models/blocks/schedule-session-block.model';
import type { TScheduleTriggerType } from '~/types/builder.types';
import { Variable } from '~/models/variable.model';
import { isFutureDate } from '~/utils/date.utils';
import {
    shortTimezoneToFullTimezone,
    getUserTimezoneWithFallback
} from '~/utils/timezone.utils';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';

type ScheduleContainerBlockDataForServer = ReturnType<
    ContainerableBlock['getDataForServer']
> &
    Pick<
        IScheduleContainerBlockResponse,
        | 'type'
        | 'schedule_session_trigger'
        | 'is_required'
        | 'session_config_id'
    >;

export class ScheduleContainerBlock extends ContainerableBlock {
    type: 'schedule_container_block';
    @observable schedule_session_trigger: TScheduleTriggerType;
    @observable is_required: boolean;
    @observable session_config: ISessionConfig;
    @observable session_config_id: string;

    constructor(
        block: IScheduleContainerBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    get displayName() {
        return 'scheduler';
    }

    get isPrintable() {
        return false;
    }

    canBeParentOf(block: TBlock) {
        return ['schedule_session_block'].includes(block.type);
    }

    getDataForServer(): ScheduleContainerBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'schedule_container_block',
            schedule_session_trigger: this.schedule_session_trigger,
            is_required: this.is_required,
            session_config_id: this.session_config_id
        };
    }

    @action
    setSessionConfig(sessionConfig: ISessionConfig) {
        this.session_config = sessionConfig;
    }

    get isDeletable() {
        return true;
    }

    get sessionConfig() {
        const sessionSelectionBlock = this.flow.sessionSelectionBlock;

        if (sessionSelectionBlock) {
            return sessionSelectionBlock.firstSelectedService?.session_config;
        }

        return this.session_config;
    }

    isDraggable() {
        return true;
    }

    @computed
    get dndType() {
        return DnDTypes.HorizontalBlock;
    }

    dndAcceptedTypes() {
        return DnDTypes.HorizontalBlock;
    }

    @computed
    get scheduleSessionBlock() {
        return this.childBlocks.find(
            block => block.type === 'schedule_session_block'
        ) as ScheduleSessionBlock;
    }

    get isActionBlock() {
        return true;
    }

    get isResponsesActionBlock() {
        return true;
    }

    @computed
    get selectedDate() {
        return this.connectedVariable?.value?.date_time_start;
    }

    @computed
    get isPastMeeting() {
        return this.isScheduled && !this.isFutureMeeting;
    }

    @computed
    get isFutureMeeting() {
        return isFutureDate(this.selectedDate);
    }

    @computed
    get displayTimezone() {
        return this.sessionTimezone || getUserTimezoneWithFallback();
    }

    @computed
    get sessionTimezone() {
        return shortTimezoneToFullTimezone[
            this.sessionConfig?.session_timezone
        ];
    }

    @computed
    get isImmediateSchedule() {
        return (
            this.schedule_session_trigger === 'immediately' ||
            this.flow.hasPaidPayments
        );
    }

    @computed
    get isBeforeChargePage() {
        const chargeBlock = this.flowRevision.chargeBlock;
        if (!chargeBlock) {
            return false;
        }

        const scheduleBlockPage = this.page;
        const chargePage = chargeBlock.page;
        return scheduleBlockPage.order < chargePage.order;
    }

    @computed
    get isAfterChargePage() {
        const chargeBlock = this.flowRevision.chargeBlock;
        if (!chargeBlock) {
            return false;
        }
        return !this.isBeforeChargePage;
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);

        if (block.session_config) {
            this.setSessionConfig(block.session_config);
        }

        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }
    }

    @computed
    private get _isRequireToPay() {
        return (
            this.isBeforeChargePage &&
            this.schedule_session_trigger === 'once_invoice_paid'
        );
    }

    @computed
    get isRequireToPay() {
        return this._isRequireToPay && !this.flow.hasPaidPayments;
    }

    @computed
    get isRequireToPayWithoutPendingMAP() {
        return (
            this._isRequireToPay && !this.flow.hasPaidPaymentsWithoutPendingMAP
        );
    }

    @computed
    get isRequireToPayVisually() {
        return this.isRequireToPay || this.isAfterChargePage;
    }

    get connectedVariable(): Variable<IScheduleContainerVariableValue> {
        return super.connectedVariable;
    }

    get variableValue() {
        return this.connectedVariable?.value;
    }

    @computed
    get sessionClient(): ScheduleContainerVariableClient {
        return this.connectedVariable?.value.client;
    }

    @computed
    private get status() {
        return this.connectedVariable?.value.status;
    }

    get isSlotSelected() {
        return false;
    }

    @computed
    get isScheduled() {
        return this.status === 'scheduled';
    }

    @computed
    get isCanceled() {
        return this.status === 'canceled';
    }

    @computed
    get isDefault() {
        return this.status === 'default' || !this.calendarItemId;
    }

    @computed
    get googleCalendarLink() {
        return this.connectedVariable?.value.google_calendar_link;
    }

    @computed
    get isCalendarItemPendingCancel() {
        return !!this.connectedVariable?.value.calendar_item_id_pending_cancel;
    }

    @computed
    get calendarItemId() {
        return this.connectedVariable?.value.calendar_item_id;
    }

    @computed
    get sessionName() {
        return this.connectedVariable?.value.session_name;
    }

    @computed
    get sessionDuration() {
        return this.connectedVariable?.value.session_duration;
    }

    @computed
    get allowCanceling() {
        return this.connectedVariable?.value.allow_canceling;
    }

    @computed
    get allowRescheduling() {
        return this.connectedVariable?.value.allow_rescheduling;
    }

    @computed
    get sessionDescription() {
        return this.connectedVariable?.value.session_description;
    }

    @computed
    get isRequireToPayAndNotYetScheduled() {
        return this.isRequireToPay && !this.isScheduled;
    }

    @computed
    get isProjectDateSessionDate() {
        return this.session_config?.set_as_project_date;
    }

    @computed
    get hasRelatedContractSmartFields() {
        return !!this.contractBlocksWithRelatedSmartFields.length;
    }

    @computed
    get contractBlocksWithRelatedSmartFields() {
        return this.flow.contractHtmlBlocks.filter(
            contract =>
                contract.page.isAfterPage(this.page) &&
                contract.hasSmartFieldsWithSourceType('session')
        );
    }

    @computed
    override get coupledBlocks(): TBlock[] {
        return this.flow.sessionSelectionBlock
            ? [this.flow.sessionSelectionBlock]
            : super.coupledBlocks;
    }
}
